import { Button, Dialog, Field, IconShare, MenuSelect, useToast } from "@dream/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { ApiError, api, unwrap } from "../api/client.ts";

/**
 * Gera o link de acompanhamento a partir do calendário operacional.
 *
 * O cliente não cria conta: abre `/c/{slug}?token=…` e vê as entregas em tempo
 * real. O token em claro só existe nesta resposta — o banco guarda o hash.
 */

interface ShareAgendaDialogProps {
  clients: Array<{ id: string; name: string }>;
  projects: Array<{ id: string; name: string; clientId: string }>;
  initialClientId?: string;
  initialProjectId?: string;
  onClose: () => void;
}

interface CreatedLink {
  url: string;
  slug: string;
  warning: string;
}

interface ShareLinkSummary {
  id: string;
  slug: string;
  clientId: string;
  projectId: string | null;
  revokedAt: string | null;
}

export function ShareAgendaDialog({
  clients,
  projects,
  initialClientId,
  initialProjectId,
  onClose,
}: ShareAgendaDialogProps) {
  const toast = useToast();
  const [clientId, setClientId] = useState(initialClientId ?? clients[0]?.id ?? "");
  const [projectId, setProjectId] = useState(initialProjectId ?? "");
  const [issued, setIssued] = useState<CreatedLink | null>(null);

  const clientProjects = useMemo(
    () => projects.filter((project) => project.clientId === clientId),
    [projects, clientId],
  );

  const existing = useQuery({
    queryKey: ["share-links", clientId],
    enabled: clientId !== "",
    queryFn: async () =>
      unwrap(
        await api.api.admin["share-links"].get({
          query: { clientId },
        }),
      ) as unknown as ShareLinkSummary[],
  });

  const activeCount = (existing.data ?? []).filter((link) => link.revokedAt === null).length;

  const createMutation = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.api.admin["share-links"].post({
          clientId,
          projectId: projectId === "" ? null : projectId,
          label: null,
          expiresAt: null,
          password: null,
        }),
      ) as unknown as CreatedLink,
    onSuccess: (data) => {
      setIssued({ url: data.url, slug: data.slug, warning: data.warning });
    },
    onError: (error: unknown) =>
      toast.error(
        "Não foi possível gerar o link",
        error instanceof ApiError ? error.message : "Tente novamente.",
      ),
  });

  async function copy(url: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiado", "Envie ao cliente. Ele não precisa de conta.");
    } catch {
      toast.error("Não foi possível copiar", "Selecione a URL e copie manualmente.");
    }
  }

  const clientName = clients.find((client) => client.id === clientId)?.name ?? "o cliente";

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Compartilhar agenda"
      description="O cliente abre o link e vê as entregas em tempo real, sem criar conta."
      size="md"
      footer={
        issued === null ? (
          <>
            <Button variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              disabled={clientId === ""}
              loading={createMutation.isPending}
              onClick={() => createMutation.mutate()}
            >
              Gerar link
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>
              Fechar
            </Button>
            <Button variant="primary" onClick={() => void copy(issued.url)}>
              Copiar link
            </Button>
          </>
        )
      }
    >
      {issued === null ? (
        <div className="space-y-4">
          <Field label="Cliente" htmlFor="share-agenda-client" required>
            <MenuSelect
              id="share-agenda-client"
              aria-label="Cliente"
              value={clientId}
              onChange={(next) => {
                setClientId(next);
                setProjectId("");
              }}
              options={clients.map((client) => ({ value: client.id, label: client.name }))}
              placeholder="Selecione o cliente"
            />
          </Field>

          <Field
            label="Restringir a um projeto"
            htmlFor="share-agenda-project"
            hint="Opcional. Sem projeto, o link mostra todas as entregas visíveis deste cliente."
          >
            <MenuSelect
              id="share-agenda-project"
              aria-label="Restringir a um projeto"
              value={projectId}
              onChange={setProjectId}
              options={[
                { value: "", label: "Toda a agenda do cliente" },
                ...clientProjects.map((project) => ({
                  value: project.id,
                  label: project.name,
                })),
              ]}
            />
          </Field>

          <div className="flex items-start gap-2.5 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2.5 text-[0.8125rem] leading-5 text-[var(--color-ink-secondary)]">
            <IconShare className="mt-0.5 size-4 shrink-0 text-[#0074FF]" />
            <div className="min-w-0">
              <p className="font-semibold text-[var(--color-ink)]">
                {clientName} não precisa de conta.
              </p>
              <p className="mt-0.5">
                O calendário dele atualiza ao vivo quando a equipe cria, move ou conclui uma
                entrega.
              </p>
            </div>
          </div>

          {activeCount > 0 && (
            <p className="text-[0.8125rem] leading-5 text-[var(--color-ink-muted)]">
              Já existe {activeCount === 1 ? "um link ativo" : `${activeCount} links ativos`} para
              este cliente. Gerar outro não revoga os anteriores. O token completo só aparece agora
              — se o link antigo foi perdido, gere este e envie o novo.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="rounded-[var(--radius-md)] border border-[var(--color-line)] bg-[var(--color-surface-subtle)] p-3">
            <code className="block break-all font-mono text-[0.75rem] leading-5 text-[var(--color-ink)]">
              {issued.url}
            </code>
          </div>
          <div
            role="alert"
            className="rounded-[var(--radius-md)] border border-amber-200 bg-amber-50 px-3 py-2 text-[0.8125rem] leading-5 text-amber-900"
          >
            {issued.warning}
          </div>
          <p className="text-[0.8125rem] leading-5 text-[var(--color-ink-muted)]">
            Envie este endereço para {clientName}. Qualquer pessoa com o link vê o calendário
            daquele cliente, atualizado em tempo real.
          </p>
        </div>
      )}
    </Dialog>
  );
}
